import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { selectFilters } from '../../reducers/filtersReducer';
import Header from '../Header/Header';
import useFilterOptions from '../../hooks/useFilterOptions';
import { selectZipCode } from '../../reducers/zipCodeReducer';
import FeaturedOffersList from '../FeaturedOffers/FeaturedOffersList';
import { device } from '../../styles/devices';
import OffersListContainer from '../OffersListContainer';
import { groupModelFamily, groupOffersByModelGroup } from '../../utils/groupOffers';
import applyConfirmedFilters from '../../utils/applyConfirmedFilters';
import { sortByDag, sortVehicleByModel } from '../../utils/sortVehiclesByModel';
import { SORTING_MODEL_CA, SORTING_MODEL_US } from '../../constants/ModelFamilyList';
import getRandomFeaturedOffers from '../../utils/getRandomFeaturedOffers';
import sortFeaturedOffers from '../../utils/sortFeaturedOffers';
import {
  FeaturedOfferSection,
  OffersListSection,
  OffersListWrapper,
  GeneralSidePadding,
  SkipToSection,
} from './OffersList.styles';
import FilterTags from '../FilterResults/FilterTags';
import ResultsCount from '../ResultsCount';
import { selectOffers } from '../../reducers/offersReducer';
import NoOffers from '../NoOffers';
import { USOffersWrapper } from '../Offers.styles';
import FilterDrawer from '../Filters/FilterDrawer';
import useScrollTracker, {
  useMainComponentScrollTracker,
  useScrollVerticalTracker,
} from '../../hooks/useScrollTracker';
import ShowFilterCTA from '../ShowFilterCTA';
import SkeletonLoader from '../../../design-system/Skeleton/Skeleton';
import NoFilteredOffers from '../NoOffers/NoFilteredOffers';
import SkipTo from '../SkipTo';
import { Divider } from '@oneaudi/unified-web-alpha-components';

type MainPageProps = {
  loading: boolean;
};

const OffersList: React.FC<MainPageProps> = ({ loading }) => {
  const isLaptop = useMediaQuery({ query: device.laptop });
  const isStickyLaptop = useMediaQuery({ query: device.laptop });
  const isBigMobile = useMediaQuery({ query: '(min-width: 480px)' });
  const isResolutionForSticky = isBigMobile && !isStickyLaptop;

  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const listRef = useRef(null);

  const { confirmedZipcode, market } = useSelector(selectZipCode);
  const { confirmedFilters } = useSelector(selectFilters);
  const { offersList } = useSelector(selectOffers);

  const isCaMarket = market === 'CA';

  const [isFiltersOpen, setIsFiltersOpen] = useState(!!isLaptop);

  const isNatOffers = confirmedZipcode === '';

  // For scrolling and animation
  // ----------------------------------------------------------------------------
  const distanceFromTopToMainComponent = useMainComponentScrollTracker(listRef);
  const isReachedEndOfContainer = useScrollVerticalTracker(listRef);
  const mobileScrolled = useScrollTracker(368);
  const scrolled = useScrollTracker(distanceFromTopToMainComponent - 28);
  // ----------------------------------------------------------------------------

  // Filtering and splitting offers logic
  // ----------------------------------------------------------------------------
  const { modelFamilyOptions } = useFilterOptions();

  const groupedModelFamilies = useMemo(() => {
    return groupModelFamily(modelFamilyOptions);
  }, [modelFamilyOptions]);

  const excludeKeys = ['vehicleType', 'offerRegion'];

  const getSelectedFiltersCount = Object.entries(confirmedFilters)
    .filter(([key]) => !excludeKeys.includes(key))
    .filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value)).length;

  const isAnyFilterSelected = useMemo(() => {
    return getSelectedFiltersCount > 0;
  }, [getSelectedFiltersCount]);

  const isMultiDagsSelected =
    confirmedFilters.offerRegion.filter((toCheck) => toCheck !== 'NAT').length > 1;

  const filteredOffers = useMemo(() => {
    return applyConfirmedFilters(offersList, confirmedFilters, isCaMarket);
  }, [offersList, isCaMarket, confirmedFilters]);

  const sortedOffers = useMemo(() => {
    // Sort by DAG for CA market, US stays as is
    const sortedByDagOffers = isCaMarket
      ? sortByDag(filteredOffers, confirmedFilters.offerRegion)
      : filteredOffers;

    // Group by different models
    const groupedOffers = groupOffersByModelGroup(sortedByDagOffers, groupedModelFamilies);
    // Order by Model
    const sortingMethod = isCaMarket ? SORTING_MODEL_CA : SORTING_MODEL_US;
    const sortedModels = sortVehicleByModel(groupedOffers, sortingMethod);
    return sortedModels;
  }, [confirmedFilters.offerRegion, filteredOffers, groupedModelFamilies, isCaMarket]);

  const featuredOffers = useMemo(() => {
    if (isAnyFilterSelected || isMultiDagsSelected) return [];

    const sortedFeaturedOffers = sortFeaturedOffers(offersList);
    const featuredOffersList = sortedFeaturedOffers.filter((offer) => offer.programPriority === 1);

    const firstSelectedDag = confirmedFilters.offerRegion[0];

    const filteredByDagOffers = isCaMarket
      ? featuredOffersList.filter((toCheck) => toCheck.offerModelDetail.dag === firstSelectedDag)
      : featuredOffersList;

    // Filter for CA market to filter by vehicle Type (no featured for CPO for now)
    const filteredByVehicleType = isCaMarket
      ? filteredByDagOffers.filter(
          (toCheck) => toCheck.offerModelDetail.vehicleType === confirmedFilters.vehicleType,
        )
      : filteredByDagOffers;

    // Filter by offer type. Only Lease offers for CA for MVP
    const filteredByOfferType = isCaMarket
      ? filteredByVehicleType.filter((toCheck) => toCheck.offerType === 'lease')
      : filteredByVehicleType;

    const featuredOffersCount = filteredByOfferType.length;
    if (featuredOffersCount < 4 && !isCaMarket) {
      const randomFeaturedOffers = getRandomFeaturedOffers(sortedFeaturedOffers);
      return randomFeaturedOffers;
    }
    return filteredByOfferType?.slice(0, 5);
  }, [
    confirmedFilters.offerRegion,
    confirmedFilters.vehicleType,
    isAnyFilterSelected,
    isCaMarket,
    isMultiDagsSelected,
    offersList,
  ]);
  // ----------------------------------------------------------------------------

  // Code realted to smooth scrolling in Skip To section
  // ----------------------------------------------------------------------------

  const handleOpenFilters = useCallback(() => {
    setIsFiltersOpen(true);
  }, []);

  const setRef = (key: string) => (el: HTMLDivElement | null) => {
    contentRefs.current[key] = el;
  };
  // ----------------------------------------------------------------------------

  const isAlpha = !process.env.IS_LEGACY;

  const RenderShowFiltersSticky = useMemo(() => {
    // Complex logic to show Show Filters
    // Case 1: laptop and filters are not open
    // Case 2, less than laptop (1024px), but more than 480px, scrolled till regular show filter dissapear
    // and filter are not opened (legacy only — alpha uses SkipTo sticky for tablet)
    if (
      (!isFiltersOpen && isStickyLaptop) ||
      (!isAlpha && mobileScrolled && !isFiltersOpen && isResolutionForSticky)
    )
      return (
        <ShowFilterCTA
          isFiltersOpen={isFiltersOpen}
          setIsFiltersOpen={setIsFiltersOpen}
          scrolled={scrolled}
        />
      );
    return null;
  }, [isAlpha, isFiltersOpen, isStickyLaptop, mobileScrolled, scrolled, isResolutionForSticky]);

  const shouldRenderNoFilteredOffers =
    isCaMarket && offersList.length && !loading && !filteredOffers.length;

  const RenderOffers = useMemo(() => {
    if ((!offersList && loading) || loading) return <SkeletonLoader />;
    if ((!offersList && !loading) || !offersList?.length) return <NoOffers />;
    return (
      <OffersListWrapper isEmptyState={filteredOffers.length === 0}>
        <GeneralSidePadding
          isFiltersOpen={isFiltersOpen}
          className={`${isAlpha ? 'alpha-header' : ''}`}
        >
          <Header
            isNational={isNatOffers}
            dag={filteredOffers[0]?.zipClusterID}
            isFiltersOpen={isFiltersOpen}
          />
        </GeneralSidePadding>
        <GeneralSidePadding
          isFiltersOpen={isFiltersOpen}
          className={`${isAlpha ? 'alpha-header' : ''}`}
        >
          <SkipToSection data-cy="skip-to-section">
            <SkipTo
              handleOpenFilters={handleOpenFilters}
              isFiltersOpen={isFiltersOpen}
              selectedFiltersCount={getSelectedFiltersCount}
            />
          </SkipToSection>
        </GeneralSidePadding>
        <GeneralSidePadding
          isFiltersOpen={isFiltersOpen}
          className={`${isAlpha ? 'alpha-header' : ''}`}
        >
          <FilterTags />
        </GeneralSidePadding>
        {isAnyFilterSelected && (
          <GeneralSidePadding
            isFiltersOpen={isFiltersOpen}
            className={`${isAlpha ? 'alpha-header' : ''} ${isAnyFilterSelected ? 'filters-selected--total-results' : ''}`}
          >
            <ResultsCount totalCount={filteredOffers.length} />
          </GeneralSidePadding>
        )}
        <FeaturedOfferSection>
          <FeaturedOffersList featuredOffers={featuredOffers} isFiltersOpen={isFiltersOpen} />
        </FeaturedOfferSection>
        {isAlpha && <Divider className="offers-divider" />}
        <GeneralSidePadding
          isFiltersOpen={isFiltersOpen}
          className={`${isAlpha ? 'alpha-header' : ''}`}
        >
          <OffersListSection
            className={`${isAnyFilterSelected ? 'filters-selected--results' : ''}`}
          >
            <OffersListContainer
              handleSectionRef={setRef}
              offers={sortedOffers}
              groupedModels={groupedModelFamilies}
              isAnyFilterSelected={isAnyFilterSelected}
              modelFamilyOptions={modelFamilyOptions}
              isFiltersOpen={isFiltersOpen}
            />
          </OffersListSection>
          {shouldRenderNoFilteredOffers && <NoFilteredOffers />}
        </GeneralSidePadding>
      </OffersListWrapper>
    );
  }, [
    featuredOffers,
    filteredOffers,
    getSelectedFiltersCount,
    groupedModelFamilies,
    handleOpenFilters,
    isAnyFilterSelected,
    isFiltersOpen,
    isNatOffers,
    loading,
    modelFamilyOptions,
    offersList,
    shouldRenderNoFilteredOffers,
    sortedOffers,
  ]);

  return (
    <USOffersWrapper ref={listRef}>
      <FilterDrawer
        isFiltersOpen={isFiltersOpen}
        setIsFiltersOpen={setIsFiltersOpen}
        isReachedEndOfContainer={isReachedEndOfContainer}
      />
      {RenderShowFiltersSticky}
      {RenderOffers}
    </USOffersWrapper>
  );
};

export default OffersList;
